/**
 * Tone Registry — Colour-Foundation Phase 1
 *
 * Named foreground/background pairs, expressed as Tailwind class strings.
 * These exist so contrast is decided ONCE, here, and consumed everywhere
 * else by name — never by picking a shade ad hoc at the call site.
 *
 * Governing rule (see tailwind.config.js `accent`/`surface`/`ink` tokens):
 *   - Gold (`accent-*`) is a MID-LUMINANCE hue. On light surfaces it may
 *     only be a FILL with `ink-deep` text on top. Gold as a FOREGROUND on
 *     a light surface must be `accent-800` (#8f5f1c, ~5.3-5.5:1) or darker.
 *   - Greys carrying content on light surfaces must be `surface-600`
 *     (~6.8-7.2:1) or darker — never `surface-400` (~2.4-2.5:1, fails AA).
 *
 * `tones.test.ts` verifies every pair below against WCAG 2.x contrast
 * thresholds (>=4.5:1) by reading the raw colour values out of
 * tailwind.config.js directly — never by regex-parsing this file.
 *
 * Pairs with an explicit `bg-*` class are self-contained (checked against
 * their own fill). Pairs that are foreground-only (no `bg-*`) are checked
 * against BOTH real light surfaces this app actually paints behind text:
 * pure white (`bg-white` cards/panels) and `surface-50` (#F7F9FB — the
 * TeacherLayout page background). See tones.test.ts `FOREGROUND_ON` map.
 */

/** Gold used as a background fill, with dark ink text/icons on top. */
export const goldFillOnLight = 'bg-accent-500 text-ink-deep';

/**
 * Gold as a foreground colour (text/icon) directly on a light surface
 * (white card, or the surface-50 page background). Never use accent-500/600
 * as a foreground on light — only as a fill (see `goldFillOnLight`).
 */
export const goldTextOnLight = 'text-accent-800';

/** Gold text/icon on a navy (`ink-deep`) surface — the existing, correct
 * pattern used by the hub tiles and hero bands (do not change this one). */
export const goldOnNavy = 'bg-ink-deep text-accent-300';

/** Muted/secondary body text or icon on a light surface. Replaces the
 * unreadable `text-surface-400` anti-pattern. */
export const mutedText = 'text-surface-600';

/**
 * The gold "tint disc / page chip" pattern — a 10%-alpha gold wash with a gold
 * glyph or label on it. Used by the media bars (MiniPlayer, PageAudioBar,
 * PageVideoBar, VideoPlayerModal) and the videos-gallery empty state.
 *
 * The old form was `bg-gold-hero/10 text-gold-hero` — gold on its own 10% wash
 * is 1.74:1, effectively invisible. `gold-hero` and `accent-500` are the same
 * hex (#e9b832), so the fill is unchanged; only the foreground moves to
 * `accent-800`. (Colour-Foundation Phase 3.)
 */
export const goldTintDisc = 'bg-accent-500/10 text-accent-800';

/**
 * Gold "solid chip" — a soft `accent-100` pill with a gold label, used for
 * badges (book-type, level code) and small icon tiles. `accent-700` on
 * `accent-100` is only 3.50:1; `accent-800` clears AA.
 */
export const goldChip = 'bg-accent-100 text-accent-800';

/**
 * The contrast-safe DARK STOP of the Spin-the-Wheel CTA gradient
 * (`bg-gradient-to-r from-games-teal to-games-teal-mid text-ink-deep`).
 *
 * Why registering one stop pins the whole gradient: a CSS gradient with no
 * explicit `color-interpolation-method` interpolates linearly through sRGB,
 * and every channel of `games-teal` (#2DD4BF) is >= the matching channel of
 * `games-teal-mid` (#14B8A6). Luminance is therefore monotonic along the
 * ramp, so the worst contrast point is exactly the darker stop — this pair.
 * The light stop is 8.35:1 against `ink-deep` for reference.
 *
 * `games-teal-deep` (#0D9488) is NOT usable as a gradient end under dark
 * text — only 4.15:1 — but it is shared with the GameWrapper header/progress
 * bands, GameTypeCard and the card signatures, so it is left untouched and
 * `teal-mid` was added alongside it instead.
 */
export const gamesTealFillOnLight = 'bg-games-teal-mid text-ink-deep';
